import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { DASHBOARD_REFRESH_MS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Package, Car, AlertTriangle, Truck, ArrowDownToLine, Clock, Shield, ExternalLink, Gauge, Fuel, Scale, FileWarning, IndianRupee } from "lucide-react";
import logger from "@/lib/logger";
import { formatTimeIST } from "@/lib/formatUtils";

const formatRupee = (n) => `\u20b9${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function StatCard({ title, value, icon: Icon, color = "blue", onClick }) {
  const colors = {
    blue: "bg-[#1B3C87]/5 border-[#1B3C87]/20 text-[#1B3C87]",
    red: "bg-[#E31E24]/5 border-[#E31E24]/20 text-[#E31E24]",
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <Card
      className={`p-3 sm:p-5 border ${colors[color]} transition-all hover:shadow-md cursor-pointer group`}
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold opacity-60">{title}</p>
          <p className="text-2xl sm:text-3xl font-black mt-1" style={{ fontFamily: "Chivo" }}>{value}</p>
        </div>
        <div className="flex flex-col items-center gap-1">
          <Icon size={24} className="opacity-40 hidden sm:block" />
          <ExternalLink size={12} className="opacity-0 group-hover:opacity-50 transition-opacity" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [calSummary, setCalSummary] = useState(null);
  const [pendingInvoices, setPendingInvoices] = useState(null);
  const [purchaseValue, setPurchaseValue] = useState(null);
  const { location, user } = useAuth();
  const navigate = useNavigate();

  const userRole = user?.role;
  const ACTIVITY_PAGE_SIZE = 20;

  // Fetch activity independently to support paging/date-range without refetching everything
  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const params = { location, limit: ACTIVITY_PAGE_SIZE, skip: activityPage * ACTIVITY_PAGE_SIZE };
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data } = await api.get("/dashboard/activity", { params });
        setActivity(data.entries || []);
        setActivityTotal(data.total || 0);
      } catch (err) {
        logger.error("Dashboard activity error:", err);
      }
    };
    fetchActivity();
  }, [location, activityPage, dateFrom, dateTo]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const calls = [
          api.get(`/dashboard/stats?location=${location}`),
          api.get(`/dashboard/activity?location=${location}`),
          api.get(`/dashboard/alerts?location=${location}`),
        ];
        if (userRole === "admin" || userRole === "manager") {
          calls.push(api.get(`/dashboard/entry-breakdown?location=${location}`));
          calls.push(api.get(`/dashboard/fleet-summary?location=${location}`));
        }
        calls.push(api.get(`/dashboard/calibration-summary?location=${location}`));
        calls.push(api.get(`/dashboard/pending-invoices?location=${location}`));
        const results = await Promise.all(calls);
        setStats(results[0].data);
        setActivity(results[1].data.entries || []);
        setAlerts(results[2].data.entries || []);
        if (userRole === "admin" || userRole === "manager") {
          if (results[3]) setBreakdown(results[3].data);
          if (results[4]) setFleet(results[4].data);
          setCalSummary(results[5]?.data || null);
          setPendingInvoices(results[6]?.data || null);
        } else {
          setCalSummary(results[3]?.data || null);
          setPendingInvoices(results[4]?.data || null);
        }
      } catch (err) {
        logger.error("Dashboard error:", err);
      }
    };
    fetchAll();
    const interval = setInterval(fetchAll, DASHBOARD_REFRESH_MS);
    return () => clearInterval(interval);
  }, [location, userRole]);

  // Today's purchase value — fetched separately so the batched dashboard calls stay index-stable
  useEffect(() => {
    const fetchPurchase = async () => {
      try {
        const { data } = await api.get(`/dashboard/purchase-value?location=${location}`);
        setPurchaseValue(data);
      } catch (err) { logger.error("Purchase value error:", err); }
    };
    fetchPurchase();
    const interval = setInterval(fetchPurchase, DASHBOARD_REFRESH_MS);
    return () => clearInterval(interval);
  }, [location]);

  const moduleMap = {
    "Visitor": "/mod1",
    "Employee": "/mod1",
    "RGP": "/mod2",
    "NRGP": "/mod2",
    "Dispatch": "/mod4",
    "Vehicle": "/mod5",
    "Incoming Consumable": "/mod3",
    "Incoming Raw Material": "/mod3",
    "Key": "/mod6",
    "Courier": "/mod6",
  };

  const alertActionMap = {
    "overdue_rgp": { path: "/mod2", label: "View RGP" },
    "key_not_returned": { path: "/mod6", label: "View Key" },
    "visitor_overstay": { path: "/mod1", label: "Checkout" },
    "vehicle_long_stay": { path: "/mod5", label: "Checkout" },
  };

  const sortedModules = useMemo(() =>
    Object.entries(breakdown?.by_module || {}).sort((a, b) => b[1] - a[1]),
    [breakdown?.by_module]
  );
  const sortedUsers = useMemo(() =>
    Object.entries(breakdown?.by_user || {}).sort((a, b) => b[1] - a[1]),
    [breakdown?.by_user]
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Dashboard</h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Gate Management System — {location}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Visitors Today" value={stats.visitors_today || 0} icon={Users} color="blue" onClick={() => navigate("/mod1")} />
        <StatCard title="Material Passes" value={stats.material_today || 0} icon={Package} color="green" onClick={() => navigate("/mod2")} />
        <StatCard title="Active Vehicles" value={stats.active_vehicles || 0} icon={Car} color="amber" onClick={() => navigate("/mod5")} />
        <StatCard title="Pending Alerts" value={stats.pending_alerts || 0} icon={AlertTriangle} color="red" onClick={() => {
          const el = document.querySelector('[data-testid="alerts-panel"]');
          el?.scrollIntoView({ behavior: "smooth" });
        }} />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Dispatches Today" value={stats.dispatches_today || 0} icon={Truck} color="blue" onClick={() => navigate("/mod4")} />
        <StatCard title="Incoming Today" value={stats.incoming_today || 0} icon={ArrowDownToLine} color="green" onClick={() => navigate("/mod3")} />
        <StatCard title="Employees Out" value={stats.employees_out || 0} icon={Clock} color="amber" onClick={() => navigate("/mod1", { state: { tab: "employee" } })} />
        <StatCard title="Visitors On-Site" value={stats.visitors_on_site || 0} icon={Shield} color="blue" onClick={() => navigate("/mod1")} />
      </div>

      {/* Entry Breakdown for Admin/Manager */}
      {breakdown && (
        <Card className="p-4 sm:p-5" data-testid="entry-breakdown">
          <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500 mb-3">Today&apos;s Entry Breakdown</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Total Entries</p>
              <p className="text-3xl font-black text-[#1B3C87]" style={{ fontFamily: "Chivo" }}>{breakdown.total_today}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">By Module</p>
              <div className="space-y-1">
                {sortedModules.map(([mod, count]) => (
                  <div key={mod} className="flex justify-between text-xs">
                    <span className="text-gray-600">{mod}</span>
                    <span className="font-bold text-[#1B3C87]">{count}</span>
                  </div>
                ))}
                {sortedModules.length === 0 && <p className="text-xs text-gray-400">No entries yet</p>}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">By User</p>
              <div className="space-y-1">
                {sortedUsers.map(([usr, count]) => (
                  <div key={usr} className="flex justify-between text-xs">
                    <span className="text-gray-600 truncate max-w-[180px]">{usr}</span>
                    <span className="font-bold text-[#1B3C87]">{count}</span>
                  </div>
                ))}
                {sortedUsers.length === 0 && <p className="text-xs text-gray-400">No entries yet</p>}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Fleet Summary */}
      {fleet && fleet.vehicles.length > 0 && (
        <Card className="p-5" data-testid="fleet-summary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500 flex items-center gap-2">
              <Car size={16} /> Fleet Summary — {fleet.month}
            </h3>
            <div className="flex gap-4 text-xs">
              <span className="text-gray-500">Total Km: <strong className="text-[#1B3C87]">{fleet.grand_km}</strong></span>
              <span className="text-gray-500">Fuel: <strong className="text-[#1B3C87]">{fleet.grand_litres} L</strong></span>
              <span className="text-gray-500">Spent: <strong className="text-[#1B3C87]">Rs. {fleet.grand_amount}</strong></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-gray-400 uppercase tracking-wider">
                  <th className="text-left py-2 font-bold">Vehicle</th>
                  <th className="text-left py-2 font-bold">Owner</th>
                  <th className="text-right py-2 font-bold">Km Run</th>
                  <th className="text-right py-2 font-bold">Trips</th>
                  <th className="text-right py-2 font-bold">Fuel (L)</th>
                  <th className="text-right py-2 font-bold">Spent (Rs.)</th>
                  <th className="text-right py-2 font-bold">Avg Mileage</th>
                </tr>
              </thead>
              <tbody>
                {fleet.vehicles.map(v => (
                  <tr key={v.vehicle_no} className="border-b border-gray-100 hover:bg-blue-50/30 cursor-pointer" onClick={() => navigate("/mod5")} data-testid={`fleet-row-${v.vehicle_no}`}>
                    <td className="py-2 font-medium">{v.vehicle_no}{v.vehicle_name ? <span className="text-gray-400 text-xs ml-1">({v.vehicle_name})</span> : ""}</td>
                    <td className="py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${v.owner_type === "Employee" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{v.owner_type}</span></td>
                    <td className="py-2 text-right font-mono">{v.total_km}</td>
                    <td className="py-2 text-right">{v.trips}</td>
                    <td className="py-2 text-right font-mono">{v.total_litres}</td>
                    <td className="py-2 text-right">{v.total_amount}</td>
                    <td className="py-2 text-right font-bold text-emerald-700">{v.avg_mileage > 0 ? `${v.avg_mileage} km/l` : "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Calibration & Pending Invoices Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Calibration History Card */}
        {calSummary && (
          <Card className="p-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/mod6")} data-testid="calibration-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500 flex items-center gap-2">
                <Scale size={16} className="text-violet-600" /> Weighbridge Calibration
              </h3>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${calSummary.status === "overdue" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                {calSummary.status === "overdue" ? "OVERDUE" : "ON TRACK"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Last Done</p>
                <p className="text-sm font-bold text-[#1B3C87]">{calSummary.last_date || "Never"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Next Due</p>
                <p className="text-sm font-bold text-amber-700">{calSummary.next_due || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Total Records</p>
                <p className="text-sm font-bold">{calSummary.total_records || 0}</p>
              </div>
            </div>
            {calSummary.trend && calSummary.trend.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1.5">Variation % Trend (Last {calSummary.trend.length})</p>
                <div className="flex items-end gap-1 h-10">
                {calSummary.trend.map((t, idx) => {
                    const pct = Math.abs(t.variation_pct);
                    const h = Math.max(4, Math.min(40, pct * 10));
                    let barColor = "bg-red-400";
                    if (pct === 0) barColor = "bg-emerald-400";
                    else if (pct < 1) barColor = "bg-amber-400";
                    return (
                      <div key={`trend-${idx}-${t.date}`} className="flex flex-col items-center flex-1">
                        <div
                          className={`w-full rounded-sm ${barColor}`}
                          style={{ height: `${h}px` }}
                          title={`${t.date}: ${t.variation_pct}%`}
                        />
                        <span className="text-[8px] text-gray-400 mt-0.5">{t.variation_pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Today's Purchase Value Card */}
        {purchaseValue && (
          <Card className="p-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/mod3")} data-testid="purchase-value-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500 flex items-center gap-2">
                <IndianRupee size={16} className="text-emerald-600" /> Today&apos;s Purchase Value
              </h3>
              <span className="text-2xl font-black text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="purchase-value-total">
                {formatRupee(purchaseValue.total_with_gst)}
              </span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-gray-500">With GST &middot; {formatRupee(purchaseValue.total_without_gst)} without GST</p>
              {purchaseValue.modules.map(m => (
                <div key={m.collection} className="flex justify-between text-xs">
                  <span className="text-gray-600">{m.label}{m.entries ? ` (${m.entries})` : ""}</span>
                  <span className={`font-bold ${m.with_gst > 0 ? "text-emerald-700" : "text-gray-300"}`}>{formatRupee(m.with_gst)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Pending Invoices Card */}
        {pendingInvoices && (
          <Card className="p-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/mod3")} data-testid="pending-invoices-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500 flex items-center gap-2">
                <FileWarning size={16} className="text-amber-600" /> Pending Invoices
              </h3>
              <span className={`text-2xl font-black ${pendingInvoices.total > 0 ? "text-amber-600" : "text-emerald-600"}`} style={{ fontFamily: "Chivo" }}>
                {pendingInvoices.total}
              </span>
            </div>
            {pendingInvoices.total > 0 ? (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500">Materials received with Challan, awaiting Invoice:</p>
                {Object.entries(pendingInvoices.by_module || {}).map(([mod, count]) => (
                  <div key={mod} className="flex justify-between text-xs">
                    <span className="text-gray-600">{mod}</span>
                    <span className="font-bold text-amber-700">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-emerald-600 font-medium">All invoices received. No pending challans.</p>
            )}
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Activity Feed */}
        <Card className="col-span-2 p-5" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-bold text-sm tracking-wider uppercase text-gray-500">
              {dateFrom || dateTo
                ? `Activity — ${dateFrom || "..."} to ${dateTo || "..."}`
                : "Recent Activity"}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setActivityPage(0); }}
                placeholder="From"
                className="h-8 text-xs border rounded px-2 bg-white"
                data-testid="activity-date-from"
              />
              <span className="text-[10px] text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setActivityPage(0); }}
                placeholder="To"
                className="h-8 text-xs border rounded px-2 bg-white"
                data-testid="activity-date-to"
              />
              {(dateFrom || dateTo) && (
                <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => { setDateFrom(""); setDateTo(""); setActivityPage(0); }} data-testid="clear-date-filter">
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-0">
            {activity.length === 0 && <p className="text-sm text-gray-400 py-4">No activity for this filter</p>}
            {activity.map((item, i) => (
              <div
                key={item.ref_no || `activity-${item.timestamp}-${i}`}
                className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-blue-50/50 rounded px-2 -mx-2 transition-colors"
                onClick={() => {
                  const path = moduleMap[item.module];
                  if (path) navigate(path);
                }}
                data-testid={`activity-item-${i}`}
              >
                <div className="text-[10px] text-gray-400 font-mono whitespace-nowrap mt-0.5">
                  {item.timestamp ? formatTimeIST(item.timestamp) : "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-700">{item.description}</span>
                  {item.ref_no && <span className="text-[10px] text-gray-400 ml-2 font-mono">{item.ref_no}</span>}
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0">{item.module}</Badge>
              </div>
            ))}
          </div>
          {activityTotal > ACTIVITY_PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs" data-testid="activity-pagination">
              <span className="text-gray-500">
                Showing {activityPage * ACTIVITY_PAGE_SIZE + 1}–{Math.min((activityPage + 1) * ACTIVITY_PAGE_SIZE, activityTotal)} of {activityTotal}
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={activityPage === 0} onClick={() => setActivityPage((p) => Math.max(0, p - 1))} data-testid="activity-prev-page">
                  Previous
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={(activityPage + 1) * ACTIVITY_PAGE_SIZE >= activityTotal} onClick={() => setActivityPage((p) => p + 1)} data-testid="activity-next-page">
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Alerts Panel */}
        <Card className="p-5" data-testid="alerts-panel">
          <h3 className="font-bold text-sm tracking-wider uppercase text-[#E31E24] mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> Active Alerts
          </h3>
          <div className="space-y-2">
            {alerts.length === 0 && <p className="text-sm text-gray-400 py-4">No active alerts</p>}
            {alerts.map((alert, i) => {
              const action = alertActionMap[alert.type];
              return (
                <div
                  key={alert.ref_no || `alert-${i}`}
                  className={`p-3 rounded border-l-4 text-sm cursor-pointer hover:shadow-sm transition-shadow ${
                    alert.severity === "high"
                      ? "bg-red-50 border-[#E31E24]"
                      : "bg-amber-50 border-amber-400"
                  }`}
                  onClick={() => action && navigate(action.path)}
                  data-testid={`alert-item-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">{alert.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{alert.description}</div>
                    </div>
                    {action && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] shrink-0"
                        onClick={(e) => { e.stopPropagation(); navigate(action.path); }}
                        data-testid={`alert-action-${i}`}
                      >
                        {action.label}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
